import { useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Loader2, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";

import { canDocflow } from "@/auth/portal";
import { loadTemplatesFn, saveTemplateFn, type TemplateRow } from "@/braining/deals.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Шаблоны документов (созвон с Дашей 28.10): постоянные куски — пункты
 * рамки и NDA, ответственность, споры, налоговые оговорки, концовки — Даша
 * правит здесь, и они уходят во все новые документы. Что меняется от
 * комплекта к комплекту (стороны, предмет, суммы, сроки, оплата),
 * вписывается в комплекте, а не здесь. Генерация строго по шаблонам, без ИИ.
 */
export const Route = createFileRoute("/templates")({
  head: () => ({ meta: [{ title: "Шаблоны документов — Портал Braining Group" }] }),
  beforeLoad: ({ context }) => {
    if (!canDocflow(context.user?.role)) throw redirect({ to: "/" });
  },
  component: TemplatesPage,
});

function TemplatesPage() {
  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [doc, setDoc] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  async function load() {
    try {
      const r = await loadTemplatesFn();
      setRows(r);
      setDoc((d) => d ?? r[0]?.doc ?? null);
    } catch (e) {
      console.error("[templates] не загрузились", e);
      toast.error("Не удалось загрузить шаблоны");
    }
  }
  useEffect(() => {
    void load();
  }, []);

  const docs = [...new Set((rows ?? []).map((r) => r.doc))];
  const q = query.trim().toLowerCase();
  const shown = (rows ?? []).filter((r) =>
    q ? (r.override ?? r.text).toLowerCase().includes(q) : r.doc === doc,
  );
  const changed = (d: string) => (rows ?? []).filter((r) => r.doc === d && r.override).length;

  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Шаблоны документов</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Постоянный текст договоров, приложений, актов, ДС и NDA. Правка идёт только в новые
          документы: комплект, отправленный АЛ, собирается по текстам на день отправки, и старые
          документы не меняются. Стороны, предмет, суммы, сроки и оплата подставляются из комплекта
          — их здесь нет.
        </p>
      </div>

      <div className="relative w-full sm:w-72">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
        <Input
          value={query}
          placeholder="Найти по тексту во всех шаблонах"
          className="h-9 pl-8"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {!q && (
        <div className="flex flex-wrap gap-1.5">
          {docs.map((d) => (
            <Button
              key={d}
              size="sm"
              variant={doc === d ? "secondary" : "ghost"}
              onClick={() => setDoc(d)}
            >
              {d}
              {changed(d) > 0 && (
                <span className="text-xs text-muted-foreground">· {changed(d)}</span>
              )}
            </Button>
          ))}
        </div>
      )}

      {rows === null ? (
        <p className="text-sm text-muted-foreground">Собираем шаблоны…</p>
      ) : (
        <div className="space-y-2">
          {shown.map((r) => (
            <TextRow key={r.key} row={r} showDoc={!!q} onSaved={() => void load()} />
          ))}
          {shown.length === 0 && (
            <p className="text-sm text-muted-foreground">Ничего не нашлось.</p>
          )}
        </div>
      )}
    </div>
  );
}

function TextRow({
  row,
  showDoc,
  onSaved,
}: {
  row: TemplateRow;
  showDoc: boolean;
  onSaved: () => void;
}) {
  const current = row.override ?? row.text;
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(row.override ?? row.text), [row.override, row.text]);
  const dirty = value.trim() !== current.trim();

  async function save(text: string | null) {
    setBusy(true);
    try {
      const r = await saveTemplateFn({ data: { key: row.key, text } });
      if (!r.ok) toast.error(r.error);
      else {
        toast.success(
          text == null
            ? "Вернули как было — для новых документов"
            : "Сохранено — пойдёт в новые документы",
        );
        onSaved();
      }
    } catch (e) {
      console.error("[templates] не сохранилось", e);
      toast.error("Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={cn(row.override && "border-primary/40")}>
      <CardContent className="space-y-2 p-3">
        {(showDoc || row.override) && (
          <p className="text-xs text-muted-foreground">
            {showDoc && row.doc}
            {showDoc && row.override && " · "}
            {row.override && "изменён"}
          </p>
        )}
        <Textarea
          value={value}
          rows={Math.max(2, Math.min(10, Math.ceil(value.length / 110)))}
          onChange={(e) => setValue(e.target.value)}
        />
        {(dirty || row.override) && (
          <div className="flex flex-wrap gap-2">
            {dirty && (
              <Button size="sm" disabled={busy} onClick={() => void save(value)}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                Сохранить
              </Button>
            )}
            {dirty && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setValue(current)}>
                Отменить
              </Button>
            )}
            {row.override && !dirty && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void save(null)}>
                <RotateCcw className="size-4" />
                Вернуть исходный текст
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
